/**
 * Patient Context Formatter
 * Formats patient medical information for AI assistant context
 */

/**
 * Generate a hidden system message with patient context
 * This message is sent to the AI assistant to provide context
 * @param {Object} patientData - Patient medical data
 * @returns {string} - Formatted patient context message
 */
export const generatePatientContextMessage = (patientData) => {
  if (!patientData) {
    return "";
  }

  const {
    age,
    gender,
    first_name,
    last_name,
    medical_background,
    lifestyle,
    allergies,
    current_treatments,
    consultations = [],
    charts = {},
    protocols = []
  } = patientData;

  let contextMessage = "PATIENT PROFILE - SYSTEM CONTEXT (Do not display this to user):\n\n";
  
  // Patient demographics
  contextMessage += "PATIENT DEMOGRAPHICS:\n";
  if (first_name || last_name) {
    contextMessage += `Name: ${first_name || ''} ${last_name || ''}\n`;
  }
  if (age) {
    contextMessage += `Age: ${age}\n`;
  }
  if (gender) {
    contextMessage += `Gender: ${gender}\n`;
  }

  // Medical background
  if (medical_background && medical_background.length > 0) {
    contextMessage += "\nMEDICAL BACKGROUND (Antécédents):\n";
    const conditions = Array.isArray(medical_background) ? medical_background : [medical_background];
    conditions.forEach(condition => {
      if (typeof condition === 'string') {
        contextMessage += `• ${condition}\n`;
      } else if (condition.name || condition.title) {
        contextMessage += `• ${condition.name || condition.title}\n`;
      }
    });
  }

  // Lifestyle
  if (lifestyle) {
    contextMessage += `\nLIFESTYLE (Style de vie):\n${lifestyle}\n`;
  }

  // Allergies
  if (allergies) {
    contextMessage += `\nALLERGIES:\n${allergies}\n`;
  }

  // Current treatments
  if (current_treatments) {
    contextMessage += `\nCURRENT TREATMENTS (Traitements en cours):\n${current_treatments}\n`;
  }

  // Recent consultations
  if (consultations && consultations.length > 0) {
    contextMessage += "\nRECENT CONSULTATIONS (Last 5):\n";
    consultations.slice(0, 5).forEach((consultation, index) => {
      const daysAgo = consultation.days_ago || 0;
      contextMessage += `\n${daysAgo} days ago - "${consultation.title || 'No title'}"\n`;
      if (consultation.observations) {
        contextMessage += `Observations: ${consultation.observations}\n`;
      }
      if (consultation.prescriptions) {
        contextMessage += `Prescriptions: ${consultation.prescriptions}\n`;
      }
    });
  }

  // Recent protocols/care steps
  if (protocols && protocols.length > 0) {
    contextMessage += "\nRECENT CARE PROTOCOLS (Last 5):\n";
    protocols.slice(0, 5).forEach((protocol) => {
      contextMessage += `• ${protocol.name || protocol.title} (${protocol.date || 'Unknown date'})\n`;
    });
  }

  // Charts/Measurements
  if (charts && Object.keys(charts).length > 0) {
    contextMessage += "\nRECENT MEASUREMENTS (Charts):\n";
    Object.entries(charts).forEach(([chartName, values]) => {
      if (Array.isArray(values) && values.length > 0) {
        const latestValue = values[0];
        contextMessage += `• ${chartName}: ${latestValue.value} (${latestValue.date || 'Unknown date'})\n`;
      }
    });
  }

  contextMessage += "\n[END OF PATIENT CONTEXT]";
  
  return contextMessage;
};

/**
 * Fetch patient medical data from local storage or context
 * @returns {Object|null} - Patient data or null
 */
export const getPatientDataFromSession = () => {
  try {
    // Try to get from sessionStorage first (most current)
    const sessionData = sessionStorage.getItem('currentPatientData');
    if (sessionData) {
      return JSON.parse(sessionData);
    }

    // Try to get from localStorage
    const localData = localStorage.getItem('currentPatientData');
    if (localData) {
      return JSON.parse(localData);
    }

    return null;
  } catch (error) {
    console.warn("Error retrieving patient data from session:", error);
    return null;
  }
};

/**
 * Store patient data in session for later use
 * @param {Object} patientData - Patient medical data
 */
export const storePatientDataInSession = (patientData) => {
  try {
    sessionStorage.setItem('currentPatientData', JSON.stringify(patientData));
  } catch (error) {
    console.warn("Error storing patient data in session:", error);
  }
};

/**
 * Extract patient info from patient object
 * Handles various API response formats
 * @param {Object} patient - Raw patient object from API
 * @returns {Object} - Normalized patient data
 */
export const normalizePatientData = (patient) => {
  if (!patient) return null;

  return {
    id: patient.id || patient.user_id,
    first_name: patient.first_name || patient.prenom || '',
    last_name: patient.last_name || patient.nom || '',
    age: patient.age || patient.age || '',
    gender: patient.gender || patient.genre || patient.sexe || '',
    medical_background: patient.medical_background || patient.antecedents || patient.medical_conditions || [],
    lifestyle: patient.lifestyle || patient.mode_de_vie || patient.style_de_vie || '',
    allergies: patient.allergies || patient.allergies || '',
    current_treatments: patient.current_treatments || patient.traitements_en_cours || patient.traitements || '',
    consultations: patient.consultations || patient.appointments || [],
    charts: patient.charts || patient.measurements || {},
    protocols: patient.protocols || patient.care_steps || []
  };
};
